// components/Molecules/HorizontalScroll/HorizontalScroll.js
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '../../Atoms/Icon/Icon';

/**
 * HorizontalScroll - Horizontal scrolling container with navigation
 * 
 * Reusable molecule for displaying items in a scrollable row.
 * Used by: ToolsShowcase, IconBullets (horizontal mode), etc.
 * 
 * Usage:
 * <HorizontalScroll items={tools}>
 *   {(tool) => <ToolCard tool={tool} />}
 * </HorizontalScroll>
 */
const HorizontalScroll = ({ 
  items = [], 
  children,
  itemWidth = 300,
  gap = 'normal',
  showArrows = true,
  className = '',
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [flashArrows, setFlashArrows] = useState(true);

  // No items, no render
  if (!items || items.length === 0) return null;

  // Monitor scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      
      if (scrollLeft > 0) {
        setFlashArrows(false);
      }
    };

    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    
    const scrollAmount = direction === 'left' ? -itemWidth : itemWidth;
    
    scrollRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
    
    setFlashArrows(false);
  };

  const gapClass = `horizontal-scroll-gap-${gap}`;

  return (
    <div className="horizontal-scroll-wrapper">
      {/* Left Arrow */}
      {showArrows && (
        <button
          onClick={() => scroll('left')}
          className={`horizontal-scroll-nav horizontal-scroll-nav-left ${
            !showLeftArrow ? 'horizontal-scroll-nav-hidden' : ''
          }`}
          aria-label="Scroll left"
        >
          <Icon name="FaChevronLeft" className="icon-sm" />
        </button>
      )}

      {/* Scrollable container */}
      <div 
        ref={scrollRef} 
        className={`horizontal-scroll ${gapClass}`}
        style={{ '--item-width': `${itemWidth}px` }}
      >
        {items.map((item, index) => (
          <div key={index} className="horizontal-scroll-item">
            {children(item, index)}
          </div>
        ))}
      </div>

      {/* Right Arrow */}
      {showArrows && (
        <button
          onClick={() => scroll('right')}
          className={`horizontal-scroll-nav horizontal-scroll-nav-right ${
            !showRightArrow ? 'horizontal-scroll-nav-hidden' : ''
          } ${
            flashArrows ? 'horizontal-scroll-nav-flash' : ''
          }`}
          aria-label="Scroll right"
        >
          <Icon name="FaChevronRight" className="icon-sm" />
        </button>
      )}
      
    </div>
  );
};

HorizontalScroll.propTypes = {
  items: PropTypes.array.isRequired,
  children: PropTypes.func.isRequired,
  itemWidth: PropTypes.number,
  gap: PropTypes.oneOf(['tight', 'normal', 'loose']),
  showArrows: PropTypes.bool,
  className: PropTypes.string,
};

export default HorizontalScroll;